import { useGlobalAlert } from "@/States/globalAlert";
import { IInputOption, Input } from "@/components/GenericComponents/input";
import { EXCEL_FORMAT } from "@/constants/general";
import { useTrans } from "@/translations";

import { useCallback, useContext, useEffect, useMemo } from "react";

import ExcelJS from "exceljs";
import { get } from "lodash";

import { ImportContext } from "../../ImportContext";
import { UploadedFileColumn, UploadedFileSheet } from "../../types";
import { UploadZone } from "./UploadZone";

const getTextValue = (value: any) =>
  get(value, "text.richText[0].text") ||
  get(value, "result") ||
  get(value, "text") ||
  (value?.formula ? "" : value);

export const UploadFile = () => {
  const { t } = useTrans();
  const {
    templates,
    setUploadedFileName,
    setUploadedFileStructure,
    selectedTemplate,
    setSelectedTemplate,
    selectedParentIds,
    setSelectedParentIds,
    reset,
  } = useContext(ImportContext);
  const { setGlobalAlert } = useGlobalAlert();

  useEffect(() => {
    if (templates.length === 1) {
      setSelectedTemplate(templates[0]);
    }
  }, [templates, setSelectedTemplate]);

  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        option: t(template.title),
        value: template.key,
      })),
    [templates, t],
  );

  const onTemplateChange = useCallback(
    (option: IInputOption) => {
      const selected = templates.find((t) => t.key === option.value);

      const canBeUsed = selected?.checkCanBeUsed();

      if (canBeUsed) {
        setSelectedTemplate(selected);
        setSelectedParentIds({});
        reset();
      } else {
        selected?.showCantBeUsed?.();
      }
    },
    [templates, setSelectedTemplate, setSelectedParentIds, reset],
  );

  const parentSelections = useMemo(
    () => selectedTemplate?.parentSelections,
    [selectedTemplate],
  );

  const updateSelectedParentIds = useCallback(
    (key: string, value: string) => {
      const newValues = {
        ...selectedParentIds,
        [key]: value,
      };

      const dependents = parentSelections?.filter((p) => p.dependsOn === key);
      if (dependents?.length) {
        dependents.forEach((d) => {
          newValues[d.key] = "";
        });
      }

      setSelectedParentIds(newValues);
    },
    [selectedParentIds, setSelectedParentIds, parentSelections],
  );

  const showUpload = useMemo(() => {
    const hasParentSelectionValues = parentSelections?.every(
      (ps) => selectedParentIds[ps.key],
    );

    return (
      !!selectedTemplate && (!parentSelections || hasParentSelectionValues)
    );
  }, [selectedTemplate, parentSelections, selectedParentIds]);

  const parentSelectionsView = useMemo(
    () =>
      parentSelections?.map((selection, i: number) => {
        const chosenPreviousOption = selectedParentIds[selection.dependsOn!];
        if (i > 0 && selection.dependsOn && !chosenPreviousOption) {
          return null;
        }

        const chosenOption = selectedParentIds[selection.key];
        const options = selection.getOptions(chosenPreviousOption);

        if (!options.length && selection.emptyComponent) {
          return <div key={selection.key}>{selection.emptyComponent}</div>;
        }

        return (
          <div key={selection.key}>
            <div className="text black font-dm-sans text-sm font-medium">
              {t(selection.label)} *
            </div>
            <div className="mb-6">
              <Input
                placeholder={t(selection.placeholder)}
                data={options}
                dropdownStyle={{ maxHeight: 190 }}
                dropdownIcon
                onChange={(option) =>
                  updateSelectedParentIds(selection.key, option.value)
                }
                ignoreOnRenderChange
                initialValue={chosenOption}
                noInitialOption={!chosenOption}
              />
            </div>
          </div>
        );
      }),
    [parentSelections, selectedParentIds, t, updateSelectedParentIds],
  );

  const onAddFile = useCallback(
    (file: File, clear: () => void) => {
      if (file.type === EXCEL_FORMAT) {
        const workbook = new ExcelJS.Workbook();
        const reader = new FileReader();

        reader.onload = () => {
          const buffer: any = reader.result || "";
          workbook.xlsx.load(buffer).then((wb) => {
            const fileData: any[] = [];

            let noData = true;
            wb.eachSheet((sheet, id) => {
              const headerValues = (
                sheet.getRow(1).values as ExcelJS.CellValue[]
              ).slice(1);

              if (sheet.actualRowCount > 1) {
                noData = false;

                const data: UploadedFileSheet = {
                  id: id.toString(),
                  name: sheet.name,
                  columns: headerValues.map((name) => ({
                    name,
                    values: [],
                  })) as UploadedFileColumn[],
                };

                sheet.eachRow((row: any, rowIndex) => {
                  if (rowIndex > 1) {
                    const rowValues = row.values.slice(1);

                    headerValues.forEach((_, i) => {
                      data.columns[i].values.push(getTextValue(rowValues[i]));
                    });
                  }
                });

                data.columns = data.columns.filter(Boolean);

                fileData.push(data);
              }
            });

            if (noData) {
              setGlobalAlert({
                type: "error",
                message: "errors:importNoData",
                instructions: "errors:importNoDataInstructions",
              });
              clear();
            } else {
              setUploadedFileName(file.name);
              setUploadedFileStructure(fileData);
            }
          });
        };
        reader.readAsArrayBuffer(file);
      } else {
        setGlobalAlert({
          type: "error",
          message: "errors:importWrongFormat",
          instructions: "importLabels:wrongFormatError",
        });
      }
    },
    [setGlobalAlert, setUploadedFileName, setUploadedFileStructure],
  );

  return (
    <div className="w-full">
      <div className="flex flex-row gap-11">
        {templates.length > 1 && (
          <div className="w-1/2">
            <div className="text black font-dm-sans text-sm font-medium">
              {t("importLabels:chooseTemplateLabel")} *
            </div>
            <div className="mb-6">
              <Input
                placeholder={t("importLabels:selectTemplate")}
                data={templateOptions}
                dropdownStyle={{ maxHeight: 190 }}
                dropdownIcon
                initialValue={selectedTemplate?.key}
                onChange={onTemplateChange}
                ignoreOnRenderChange
                noInitialOption={!selectedTemplate}
              />
            </div>

            {parentSelectionsView}
          </div>
        )}

        <div className="w-1/2 pt-5 pr-16">
          {showUpload && <UploadZone onAddFile={onAddFile} />}
        </div>
      </div>
    </div>
  );
};
